import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import 'dayjs/locale/it';
import dayjs from 'dayjs';
import {
  TextField,
  Button,
  Grid,
  Autocomplete,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  createCompany,
  updateCompany,  
  fetchCompanyTypes,
} from "../../features/company/companyThunk";
import {
  selectIsLoading,
  selectError,
  selectCompanyTypes,
} from "../../features/company/companySlice";
import { selectCities, selectProvincies, selectRegions } from "../../features/region/regionSlice";
import { fetchCities, fetchProvincies, fetchRegions } from "../../features/region/regionThunk";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import moment from "moment/moment";
dayjs.locale('it');

const schema = yup.object().shape({
  ragioneSociale: yup.string().required("Ragione sociale è obbligatoria"),
  cfPiva: yup.string().required("CF/P.IVA è obbligatoria"),
  tipoAzienda: yup.string().required("Tipo di azienda è obbligatoria"),
  codRegistro: yup.string().required("Cod. Registro imprese è obbligatoria"),
  codAteco: yup.string().required("Cod. ATECO è obbligatoria"),
  dataCostituzione: yup.string().required("Data costituzione è obbligatoria"),
  indirizzo: yup.string().required("Indirizzo è obbligatoria"),
  citta: yup.string().required("Città è obbligatoria"),
  cap: yup.string()
  .required("CAP è obbligatoria")
  .matches(/^\d{5}$/, "Il CAP deve essere composto esattamente da 5 cifre"),
  fr: yup.string().required("FR è obbligatoria"),
  regione: yup.string().required("Regione è obbligatoria"),
  nazione: yup.string().required("Nazione è obbligatoria"),
  telefono: yup.string().required("Telefono è obbligatoria"),
  email: yup.string().email("Invalid email").required("Email è obbligatoria"),
  pec: yup.string().required("PEC è obbligatoria"),
  sitoWeb: yup.string().required("Sito web è obbligatoria"),
  nomeResponsabile: yup.string().required("Nome è obbligatoria"),
  numeroResponsabile: yup.string().required("Numero è obbligatoria"),
  mailResponsabile: yup
    .string()
    .email("Email non valida")
    .required("Mail è obbligatoria"),
    telefono: yup
    .string()
    .required("Telefono è obbligatoria")
    .matches(/^\d+$/, "Il telefono deve contenere solo numeri"),
});

const AziendaFrom = ({ selectedCompany, onClose }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isLoading = useSelector(selectIsLoading);
  const error = useSelector(selectError);
  const companyTypes = useSelector(selectCompanyTypes);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { cities } = useSelector((state) => state.region);
  const regions = useSelector(selectRegions);
  const provincies = useSelector(selectProvincies);

  useEffect(() => {
    dispatch(fetchCompanyTypes());
    dispatch(fetchCities());
    dispatch(fetchRegions());
    dispatch(fetchProvincies());
  }, [dispatch]);

  // Convert string date to dayjs object or use null if empty
  const getInitialDate = (dateString) => {
    if (!dateString) return null;
    const date = dayjs(dateString);
    return date.isValid() ? date : null;
  };

  const defaultValues = {
    ragioneSociale: selectedCompany ? selectedCompany.name : "",
    cfPiva: selectedCompany ? selectedCompany.cf_piva : "",
    tipoAzienda: selectedCompany ? selectedCompany.company_type_id : null,
    codRegistro: selectedCompany ? selectedCompany.company_register_code : "",
    codAteco: selectedCompany ? selectedCompany.ateco_code : "",
    dataCostituzione: selectedCompany 
    ? getInitialDate(
        selectedCompany.date_of_constitution 
          ? moment(selectedCompany.date_of_constitution).format('YYYY-MM-DD') 
          : null
      ) 
    : null,
  
    indirizzo: selectedCompany ? selectedCompany.address : "",
    citta: selectedCompany ? selectedCompany.city : "",
    cap: selectedCompany ? selectedCompany.cap : "",
    fr: selectedCompany ? selectedCompany.fr : "",
    regione: selectedCompany ? selectedCompany.region : "",
    nazione: selectedCompany ? selectedCompany.nation : "",
    telefono: selectedCompany ? selectedCompany.telephone : "",
    email: selectedCompany ? selectedCompany.email : "",
    pec: selectedCompany ? selectedCompany.pec : "",
    sitoWeb: selectedCompany ? selectedCompany.website : "",
    nomeResponsabile: selectedCompany ? selectedCompany.manager_name : "",
    numeroResponsabile: selectedCompany ? selectedCompany.manager_number : "",
    mailResponsabile: selectedCompany ? selectedCompany.manager_email : "",
  };
  
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues, 
  });

  useEffect(() => {
    reset(defaultValues);
  }, [selectedCompany, reset]);

  const onSubmit = async (formData) => {
    // Prevent duplicate submissions
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    const apiData = {
      // Company Details
      name: formData.ragioneSociale,
      cf_piva: formData.cfPiva,
      company_type_id: formData.tipoAzienda,
      company_register_code: formData.codRegistro,
      ateco_code: formData.codAteco,
      date_of_constitution: formData.dataCostituzione && dayjs(formData.dataCostituzione).isValid() 
  ? dayjs(formData.dataCostituzione).format('YYYY-MM-DD') 
  : null,      
      // Address Details
      address: formData.indirizzo,
      city: formData.citta,
      cap: formData.cap,
      fr: formData.fr,
      region: formData.regione,
      nation: formData.nazione,
      
      // Contact Details
      telephone: formData.telefono,
      email: formData.email,
      pec: formData.pec,
      website: formData.sitoWeb,
      
      // Manager Details
      manager_name: formData.nomeResponsabile,
      manager_number: formData.numeroResponsabile,
      manager_email: formData.mailResponsabile,
    };

    try {
      if (selectedCompany) {
        await dispatch(updateCompany({ id: selectedCompany.id, companyData: apiData })).unwrap();
      } else {
        await dispatch(createCompany(apiData)).unwrap();
      }
      setSubmissionSuccess(true);
      reset();
      setTimeout(() => {
        if (onClose) onClose();
      }, 1500);
    } catch (err) {
      console.error("Company submission failed:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const companyTypeOptions = companyTypes.length > 0 ? companyTypes : [];
  const nationOptions = ["Italia", "Francia", "Germania", "Spagna"];

  // Create a memoized submit handler to prevent recreation on each render
  const memoizedSubmitHandler = React.useCallback(
    handleSubmit(onSubmit),
    [handleSubmit, onSubmit]
  );

  return (
    <>
      {submissionSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {selectedCompany ? "Azienda aggiornata con successo!" : "Azienda registrata con successo!"}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form className="AziendaFrom" onSubmit={memoizedSubmitHandler}>
        <Controller
          name="ragioneSociale"
          control={control}
          render={({ field }) => (
            <TextField
              className="customTextBox"
              {...field}
              label="Ragione sociale"
              fullWidth
              margin="normal"
              error={!!errors.ragioneSociale}
              helperText={errors.ragioneSociale?.message}
            />
          )}
        />
        <Controller
          name="cfPiva"
          control={control}
          render={({ field }) => (
            <TextField
              className="customTextBox"
              {...field}
              label="CF/P.IVA"
              fullWidth
              margin="normal"
              error={!!errors.cfPiva}
              helperText={errors.cfPiva?.message}
            />
          )}
        />
        <Controller
          name="tipoAzienda"
          control={control}
          render={({ field }) => (
            <Autocomplete
              value={
                companyTypeOptions.find((option) => option.id === field.value) || null
              }
              options={companyTypeOptions}
              getOptionLabel={(option) => option.name || ""}
              onChange={(event, newValue) => field.onChange(newValue ? newValue.id : null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tipo di azienda"
                  className="customTextBox autoCompleteTextBox"
                  margin="normal"
                  fullWidth
                  error={!!errors.tipoAzienda}
                  helperText={errors.tipoAzienda?.message}
                />
              )}
            />
          )}
        />
        <Controller
          name="codRegistro"
          control={control}
          render={({ field }) => (
            <TextField
              className="customTextBox"
              {...field}
              label="Cod. Registro imprese"
              fullWidth
              margin="normal"
              error={!!errors.codRegistro}
              helperText={errors.codRegistro?.message}
            />
          )}
        />
        <Controller
          name="codAteco"
          control={control}
          render={({ field }) => (
            <TextField
              className="customTextBox"
              {...field}
              label="Cod. ATECO"
              fullWidth
              margin="normal"
              error={!!errors.codAteco}
              helperText={errors.codAteco?.message}
            />
          )}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
          <Controller
            name="dataCostituzione"
            control={control}
            render={({ field }) => (
              <DatePicker
                label="Data Costituzione"
                className="datePickerField"
                value={field.value}
                onChange={(newValue) => field.onChange(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "normal",
                    error: !!errors.dataCostituzione,
                    helperText: errors.dataCostituzione?.message
                  }
                }}
              />
            )}
          />
        </LocalizationProvider>
        <label className="formLabel">Sede legale</label>
        <Grid container spacing={0}>
          <Grid size={12}>
            <Controller
              name="indirizzo"
              control={control}
              render={({ field }) => (
                <TextField
                  className="customTextBox"
                  {...field}
                  label="Indirizzo"
                  fullWidth
                  margin="normal"
                  error={!!errors.indirizzo}
                  helperText={errors.indirizzo?.message}
                />
              )}
            />
          </Grid>
        </Grid>
        
        {/* Rest of the form remains the same */}
        <Grid container spacing={1}>
          <Grid size={8}>
            <Controller
              name="citta"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={cities?.map((city) => city.comune) || []}
                  value={field.value || ""}
                  onChange={(event, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Città"
                      className="customTextBox autoCompleteTextBox"
                      margin="normal"
                      fullWidth
                      error={!!errors.citta}
                      helperText={errors.citta?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={4}>
            <Controller
              name="cap"
              control={control}
              render={({ field }) => (
                <TextField
                  className="customTextBox"
                  {...field}
                  label="CAP"
                  fullWidth
                  margin="normal"
                  error={!!errors.cap}
                  helperText={errors.cap?.message}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid size={3}>
            <Controller
              name="fr"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={provincies ? provincies.map((fr) => fr.sigla) : []}
                  value={field.value || ""}
                  onChange={(event, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="PR"
                      className="customTextBox autoCompleteTextBox"
                      margin="normal"
                      fullWidth
                      error={!!errors.fr}
                      helperText={errors.fr?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={5}>
            <Controller
              name="regione"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={regions?.map((regione) => regione.regione) || []}
                  value={field.value || ""}
                  onChange={(event, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Regione"
                      className="customTextBox autoCompleteTextBox"
                      margin="normal"
                      fullWidth
                      error={!!errors.regione}
                      helperText={errors.regione?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={4}>
            <Controller
              name="nazione"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={nationOptions}
                  value={field.value || ""}
                  onChange={(event, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Nazione"
                      className="customTextBox autoCompleteTextBox"
                      margin="normal"
                      fullWidth
                      error={!!errors.nazione}
                      helperText={errors.nazione?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>
        </Grid>
        <label className="formLabel">Contatti</label>
        <Controller
          name="telefono"
          control={control}
          render={({ field }) => (
            <TextField
              className="customTextBox"
              {...field}
              label="Telefono"
              fullWidth
              margin="normal"
              error={!!errors.telefono}
              helperText={errors.telefono?.message}
            />
          )}
        />
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              className="customTextBox"
              {...field}
              label="Email"
              fullWidth
              margin="normal"
              error={!!errors.email}
              helperText={errors.email?.message}
            />
          )}
        />
        <Controller
          name="pec"
          control={control}
          render={({ field }) => (
            <TextField
              className="customTextBox"
              {...field}
              label="PEC"
              fullWidth
              margin="normal"
              error={!!errors.pec}
              helperText={errors.pec?.message}
            />
          )}
        />
        <Controller
          name="sitoWeb"
          control={control}
          render={({ field }) => (
            <TextField
              className="customTextBox"
              {...field}
              label="SitoWeb"
              fullWidth
              margin="normal"
              error={!!errors.sitoWeb}
              helperText={errors.sitoWeb?.message}
            />
          )}
        />
        <label className="formLabel">Responsabile aziendale</label>
        <Controller
          name="nomeResponsabile"
          control={control}
          render={({ field }) => (
            <TextField
              className="customTextBox"
              {...field}
              label="Nome"
              fullWidth
              margin="normal"
              error={!!errors.nomeResponsabile}
              helperText={errors.nomeResponsabile?.message}
            />
          )}
        />
        <Controller
          name="numeroResponsabile"
          control={control}
          render={({ field }) => (
            <TextField
              className="customTextBox"
              {...field}
              label="Numero"
              fullWidth
              margin="normal"
              error={!!errors.numeroResponsabile}
              helperText={errors.numeroResponsabile?.message}
            />
          )}
        />
        <Controller
          name="mailResponsabile"
          control={control}
          render={({ field }) => (
            <TextField
              className="customTextBox"
              {...field}
              label="Mail"
              fullWidth
              margin="normal"
              error={!!errors.mailResponsabile}
              helperText={errors.mailResponsabile?.message}
            />
          )}
        />

        <div className="btnPart1">
          <Button 
            type="submit" 
            variant="contained" 
            disabled={isLoading || isSubmitting}
          >
            {isLoading ? <CircularProgress size={24} /> : "Salva dati"}
          </Button>
        </div>
      </form>
    </>
  );
};

export default AziendaFrom;